import { createClient, EntrySkeletonType } from 'contentful';
import { Document } from '@contentful/rich-text-types';

interface BlogPostFields {
  internalName: string;
  seoFields: {
    fields: {
      title: string;
      description: string;
      keywords: string[];
      ogImage?: {
        fields: {
          file: {
            url: string;
          };
        };
      };
    };
  };
  slug: string;
  author: {
    fields: {
      name: string;
      // Add other author fields as needed
    };
  };
  publishedDate: string;
  title: string;
  shortDescription: string;
  featuredImage: {
    fields: {
      file: {
        url: string;
      };
      title: string;
    };
  };
  content: Document; // Rich Text content
  markdownContent?: string; // Optional markdown content
  embedHtml?: string; // Optional field for HTML embeds like Buzzsprout
  relatedBlogPosts?: Array<{
    fields: {
      title: string;
      slug: string;
    };
  }>;
}

interface BlogPostSkeleton extends EntrySkeletonType {
  fields: BlogPostFields;
  contentTypeId: 'pageBlogPost';  
}

export type BlogPost = BlogPostFields;

interface ResourceFields {
  title: string;
  description: Document;
  link: string;
  tags: string[];
}

interface ResourceSkeleton extends EntrySkeletonType {
  fields: ResourceFields;
  contentTypeId: 'resource';
}

export type Resource = ResourceFields;

const client = createClient({
  space: import.meta.env.VITE_CONTENTFUL_SPACE_ID,
  accessToken: import.meta.env.VITE_CONTENTFUL_CDA_TOKEN,
});

export const getBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const response = await client.getEntries<BlogPostSkeleton>({
      content_type: 'pageBlogPost',
      order: ['sys.createdAt'],
    });

    console.log('Fetched blog posts:', response.items);

    return response.items.map((item) => ({
      ...item.fields,
    }));
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return [];
  }
};

export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const query = {
      content_type: 'pageBlogPost',
      include: 2,
      [`fields.slug`]: slug,
      limit: 1,
    } as const;

    const response = await client.getEntries<BlogPostSkeleton>(query);

    if (response.items.length === 0) {
      return null;
    }

    console.log('Fetched blog post:', response.items[0]);

    return {
      ...response.items[0].fields,
    };
  } catch (error) {
    console.error('Error fetching blog post:', error);
    return null;
  }
};

export const getResources = async (): Promise<Resource[]> => {
  try {
    const response = await client.getEntries<ResourceSkeleton>({
      content_type: 'resource',
      order: ['sys.createdAt'],
    });

    console.log('Fetched resources:', response.items);

    return response.items.map((item) => ({
      ...item.fields,
    }));
  } catch (error) {
    console.error('Error fetching resources:', error);
    return [];
  }
};
